import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { AppLauncher } from "@capacitor/app-launcher";
import { fansApi } from "../services/fans";
import { fanReloadAfterVerify } from "../lib/fanAccount";
import { instagramApi } from "../services/instagram";
import { openExternal } from "../lib/openExternal";
import type { IgDmVerifyStart, IgDmVerifyStatus } from "../types/fans";

// "Verify with a DM" (server lib/identity/instagramDm.js): the fan proves the
// Instagram handle is theirs by sending @request_app_official one message through a
// one-time ig.me link. This hook is the round trip:
//
//   idle → (Connect) starting → waiting ──(the DM arrives)──→ idle + onVerified
//                                     ├─(30 minutes pass)──→ expired
//                                     ╰─(another talent holds that Instagram)──→ taken
//
// The talent's backup (components/TalentIgConnect, 1.5.3) runs the same round
// trip on the talent's endpoints: pass TALENT_DM_RAIL — or, for a talent still
// signing up (build 54), the sign-up's own rail (no account yet: the proof waits
// on a claim). Only a talent can end "taken" (one Instagram vouches for one
// talent); a fan's Instagram simply moves to the newest fan who proves it.
//
// In the app, Instagram opens by itself (a universal link: the Instagram app
// when it's installed, the browser otherwise) — unless the caller starts
// without a tap (start({ open: false }): the talent just came back from
// Instagram's sign-in), when "Open Instagram" is theirs to tap. On the web a
// browser won't open a new page after a server round trip, so the panel's
// "Open Instagram" button is the fan's own tap.
//
// While waiting it asks every 3 seconds, and at once when the fan comes back
// from Instagram — the moment they'd look for the tick. A check caught when the
// phone hopped to Instagram may never answer, so it can't hold up the next one
// (founder 2026-11-03: he came back, and it only noticed after a second tap).

export type IgDmPhase = "idle" | "starting" | "waiting" | "expired" | "taken" | "failed";

/** Whose round trip: the signed-in fan's (default) or the signed-in talent's. */
export type IgDmRail = {
  start: () => Promise<IgDmVerifyStart>;
  status: (sessionId: string) => Promise<IgDmVerifyStatus>;
  /** Re-read the account once Instagram vouched (before onVerified runs). */
  afterVerified: () => Promise<unknown>;
};

export const FAN_DM_RAIL: IgDmRail = {
  start: () => fansApi.startInstagramDmVerify(),
  status: (sessionId) => fansApi.instagramDmVerifyStatus(sessionId),
  // The fan's handle, now verified, everywhere it shows — the header chip too.
  afterVerified: () => fanReloadAfterVerify(),
};

export const TALENT_DM_RAIL: IgDmRail = {
  start: () => instagramApi.startDmVerify(),
  status: (sessionId) => instagramApi.dmVerifyStatus(sessionId),
  // The talent's screen re-reads the profile itself (its onVerified).
  afterVerified: async () => null,
};

const POLL_MS = 3000;
// How long one check may hold up the next.
const CHECK_TIMEOUT_MS = 8000;
// The server keeps judging a DM sent in time; give a late one a minute.
const GRACE_MS = 60_000;

// On a computer (the website with a mouse) the link is a QR to scan with the
// phone: Instagram's website drops the one-time code from the link; the
// phone's Instagram app keeps it.
export const onComputer = () =>
  !Capacitor.isNativePlatform() && typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches;

// In the app: hand the link to the phone, which opens the Instagram app.
// (The web uses a plain link the fan taps — see FanConnectFlow, IgConnectRow.)
export function openInstagramInApp(url: string) {
  AppLauncher.openUrl({ url }).catch(() => openExternal(url));
}

export function useInstagramDmVerify(onVerified: (username: string) => void, rail: IgDmRail = FAN_DM_RAIL) {
  const [phase, setPhase] = useState<IgDmPhase>("idle");
  const [link, setLink] = useState<IgDmVerifyStart | null>(null);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;
  const railRef = useRef(rail);
  railRef.current = rail;

  const start = useCallback(async (opts?: { open?: boolean }) => {
    setPhase("starting");
    try {
      const next = await railRef.current.start();
      setLink(next);
      setPhase("waiting");
      if (Capacitor.isNativePlatform() && opts?.open !== false) openInstagramInApp(next.url);
    } catch {
      setPhase("failed");
    }
  }, []);

  const cancel = useCallback(() => {
    setLink(null);
    setPhase("idle");
  }, []);

  useEffect(() => {
    if (phase !== "waiting" || !link) return;
    let alive = true;
    let busy = false;
    let lastCheck = 0;
    const finish = (next: IgDmPhase) => {
      if (!alive) return;
      alive = false;
      setLink(null);
      setPhase(next);
    };
    const check = async (comingBack = false) => {
      if (!alive) return;
      if (!comingBack && (busy || document.visibilityState === "hidden")) return;
      busy = true;
      const thisCheck = ++lastCheck;
      const release = () => {
        if (thisCheck === lastCheck) busy = false;
      };
      const stuck = window.setTimeout(release, CHECK_TIMEOUT_MS);
      try {
        const r = await railRef.current.status(link.sessionId);
        if (r.status === "verified" && r.username) {
          await railRef.current.afterVerified().catch(() => null);
          if (alive) onVerifiedRef.current(r.username);
          finish("idle");
        } else if (r.status === "taken") {
          finish("taken");
        } else if (r.status !== "pending") {
          finish("expired");
        }
      } catch (err: any) {
        // 404 = the link is gone; anything else (venue Wi-Fi) just tries again.
        if (err?.response?.status === 404) finish("expired");
      } finally {
        window.clearTimeout(stuck);
        release();
      }
    };
    const timer = window.setInterval(() => void check(), POLL_MS);
    const onBack = () => {
      if (document.visibilityState === "visible") void check(true);
    };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    // The app's own word that it's back: the phone's page events can come late.
    const resumed = Capacitor.isNativePlatform() ? App.addListener("resume", () => void check(true)) : null;
    const giveUp = window.setTimeout(
      () => void check().then(() => finish("expired")),
      Math.max(0, new Date(link.expiresAt).getTime() + GRACE_MS - Date.now())
    );
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.clearTimeout(giveUp);
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
      resumed?.then((listener) => listener.remove()).catch(() => {});
    };
  }, [phase, link]);

  return {
    phase,
    url: link?.url || null,
    // What the copy names (the server's setting; request_app_official on older servers).
    handle: link?.handle || "request_app_official",
    qrSvg: link?.qrSvg || null,
    start,
    cancel,
  };
}

export type InstagramDmVerify = ReturnType<typeof useInstagramDmVerify>;
